<template>
  <nav class="links">
    <ul class="links__list">
      <li
        :class="{
          active: route.name === 'swap',
        }"
        class="links__list-item"
      >
        <router-link class="link" :to="{ name: 'swap' }">
          <div v-if="!isSmallDevice" class="link__icon link__icon_type_swap" />
          {{ $t('swap.swap') }}
        </router-link>
      </li>
      <li
        class="links__list-item"
        :class="{
          active: ['pools', 'stakes', 'poolsSolo', 'aptos-staking'].includes(
            route.name?.toString() ?? '',
          ),
        }"
      >
        <router-link v-if="isL2" class="link" :to="{ name: 'pools' }">
          <div class="link__icon link__icon_type_earn" />
          {{ $t('header.pools') }}
        </router-link>
        <button
          v-else
          class="link link_type_button"
          aria-haspopup="true"
          aria-controls="link__dropdown"
          name="earn"
          @click="toggleMenu"
        >
          <div v-if="!isSmallDevice" class="link__icon link__icon_type_earn" />
          {{ $t('header.earn') }}
          <i
            class="link__angle"
            :class="
              showEarnLinksList
                ? 'link__angle_toggle_yes'
                : 'link__angle_toggle_no'
            "
          />
        </button>
        <p-menu
          id="link__dropdown"
          ref="earnMenu"
          class="link__dropdown big"
          :model="earnLinks"
          :popup="true"
          @hide="hideEarnMenuHandler"
        >
        </p-menu>
      </li>
      <li
        v-if="showBridge"
        :class="{ active: route.name === 'bridge' }"
        class="links__list-item"
        @click="trackToMixpanel('bridge')"
      >
        <button
          class="link link_type_button"
          aria-haspopup="true"
          aria-controls="link__dropdown"
          name="bridge"
          @click="toggleMenu"
        >
          <div
            v-if="!isSmallDevice"
            class="link__icon link__icon_type_bridge"
          />
          {{ $t('header.bridge') }}
          <i
            class="link__angle"
            :class="
              showBridgeLinksList
                ? 'link__angle_toggle_yes'
                : 'link__angle_toggle_no'
            "
          />
        </button>
        <p-menu
          id="link__dropdown"
          ref="bridgeMenu"
          class="link__dropdown"
          :model="bridgeLinks"
          :popup="true"
          @hide="hideBridgeMenuHandler"
        >
          <template #item="{ item }">
            <a
              v-if="item.href"
              :href="item.href"
              class="p-menuitem-link"
              role="menuitem"
              tabindex="0"
              rel="noreferrer noopener"
              @click="item.command"
            >
              <span class="p-menuitem-icon" :class="item.icon"></span>
              <span class="p-menuitem-text">{{ item.label }}</span>
            </a>
            <router-link
              v-else
              class="p-menuitem-link menuitem-link-swap"
              :to="{ name: item.to.name }"
              exact
              :exact-active-class="
                route.name === item.to.name ? 'extract-active-item' : ''
              "
              @click="item.command"
            >
              <span
                v-if="item.icon"
                class="p-menuitem-icon"
                :class="item.icon"
              />
              {{ item.label }}
            </router-link>
          </template>
        </p-menu>
      </li>
      <li
        v-if="!isL2 && !isMovement"
        :class="{ active: route.name === 'stats' }"
        class="links__list-item"
        @click="trackToMixpanel('stats')"
      >
        <router-link class="link" :to="{ name: 'stats' }">
          <div v-if="!isSmallDevice" class="link__icon link__icon_type_stats" />
          {{ $t('header.stats') }}
        </router-link>
      </li>
      <li
        v-if="!isL2"
        :class="{ active: route.name === 'cl' }"
        class="links__list-item"
        @click="trackToMixpanel('concentrated-liquidity')"
      >
        <a class="link" :href="CL_URL" rel="noreferrer noopenner">
          <div v-if="!isSmallDevice" class="link__icon link__icon_type_cl" />
          Concentrated Liquidity
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMediaQuery } from '@vueuse/core';
import {
  AVAILABLE_FUNCTIONS,
  CORRECT_CHAIN,
  FARM_URL,
  CL_URL,
  ChainsEnum,
} from '@/constants';
import { useI18n } from '@/composables/useI18n';
import { EMixpanelEventNames, EMixpanelProvideKeys } from '@/types/enums';

const { t } = useI18n();

enum ELinksNames {
  swap = 'swap',
  pools = 'pools',
  bridge = 'bridge',
  stats = 'stats',
  kana = 'kana',
}

const track: any = inject(EMixpanelProvideKeys.Track);

const route = useRoute();

const isL2 = computed(() => CORRECT_CHAIN.id === ChainsEnum['lumio-testnet']);
const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

const showBridge = computed(
  () => CORRECT_CHAIN.id === ChainsEnum['aptos-mainnet'],
);

const hasStakes = computed(() => AVAILABLE_FUNCTIONS.STAKES);
const hasAptosStaking = computed(
  () => AVAILABLE_FUNCTIONS.APTOS_STAKING && !isMovement.value,
);
// const hasSolo = computed(() => AVAILABLE_FUNCTIONS.SOLO);

const earnLinks = [
  {
    label: t('header.pools'),
    name: ELinksNames.pools,
    to: { name: 'pools' },
    icon: 'dropdown-link dropdown-link_type_pools',
    command: () => {
      track(EMixpanelEventNames.PoolsClicked);
    },
  },
  hasStakes.value &&
    !isMovement.value && {
      label: t('header.farms'),
      name: 'stakes',
      icon: 'dropdown-link dropdown-link_type_farms',
      command: () => {
        track(EMixpanelEventNames.FarmClicked);
        window.open(FARM_URL, '_self', 'noopener,noreferrer');
      },
    },
  {
    label: t('Single-Token Deposits'),
    name: 'poolsSolo',
    to: { name: 'poolsSolo' },
    icon: 'dropdown-link dropdown-link_type_solo',
    command: () => {
      track(EMixpanelEventNames.SoloClicked);
    },
  },
  hasAptosStaking.value && {
    label: t('header.staking'),
    name: 'aptos-staking',
    to: { name: 'aptos-staking' },
    icon: 'dropdown-link dropdown-link_type_staking',
    command: () => track(EMixpanelEventNames.StakingClicked),
  },
].filter(Boolean);

const bridgeLinks = [
  {
    label: 'LayerZero',
    name: ELinksNames.bridge,
    href: 'https://bridge.liquidswap.com',
    icon: 'dropdown-link dropdown-link_type_layerzero',
  },
  {
    label: 'Wormhole',
    name: ELinksNames.bridge,
    href: 'https://bridge.liquidswap.com/wormhole-widget',
    icon: 'dropdown-link dropdown-link_type_wormhole',
    command: () => {
      bridgeMenu.value.toggle(false);
      track(EMixpanelEventNames.BridgeClicked);
    },
  },
];

const showEarnLinksList = ref(false);
const showBridgeLinksList = ref(false);
const earnMenu = ref();
const bridgeMenu = ref();
const toggleEarnMenu = () => {
  showEarnLinksList.value = !showEarnLinksList.value;
};

const toggleBridgeMenu = () => {
  showBridgeLinksList.value = !showBridgeLinksList.value;
};

const toggleMenu = (event: MouseEvent) => {
  const menuName = (event.currentTarget as HTMLButtonElement).getAttribute(
    'name',
  ) as string;
  if (menuName && menuName === 'earn') {
    toggleEarnMenu();
    earnMenu.value.toggle(event);
  }
  if (menuName && menuName === 'bridge') {
    toggleBridgeMenu();
    bridgeMenu.value.toggle(event);
  }
};

const hideEarnMenuHandler = () => {
  if (showEarnLinksList.value) {
    toggleEarnMenu();
  }
};

const hideBridgeMenuHandler = () => {
  if (showBridgeLinksList.value) {
    toggleBridgeMenu();
  }
};

function trackToMixpanel(type: 'stats' | 'bridge') {
  if (type === 'stats') {
    track(EMixpanelEventNames.StatClicked);
    return;
  }

  track(EMixpanelEventNames.BridgeClicked);
}

const isSmallDevice = useMediaQuery('(max-width: 1600px)');
</script>

<style lang="scss" scoped>
.links {
  display: flex;
  align-items: center;
  justify-content: center;

  height: 40px;
  padding: 0 1rem;

  @media screen and (max-width: 1600px) {
    padding: 0 0.5rem;
  }

  @media screen and (max-width: 1400px) {
    display: none;
  }

  &__list {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: max-content;
    padding: 0;
    margin: 0;
    list-style-type: none;
  }

  &__list-item {
    position: relative;
    height: 40px;
    margin-right: 32px;
    border-radius: 8px;
    font-weight: 500;
    font-family: var(--font-family);
    transition: background-color 0.15s ease;
    will-change: background-color, color;

    &.active,
    &:hover {
      background-color: #3e3e52;
      color: #fff;
    }

    &::before {
      position: absolute;
      top: 8px;
      left: -16px;

      display: block;
      width: 0;
      height: 24px;

      border-right: 1px solid #494954;
      border-radius: 8px;
      content: '';

      @media screen and (max-width: 1600px) {
        border-right: 0;
      }
    }

    @media screen and (max-width: 1600px) {
      margin-right: 8px;

      &::before {
        display: none;
      }
    }
  }
}

.link {
  align-items: center;
  display: flex;
  height: 100%;
  padding: 0.5rem;

  border-radius: 8px;

  font-size: 0.9375rem;
  color: #e5e4fa;

  transition: background-color 0.15s ease;
  will-change: background-color;

  &_type_button {
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 100%;
    height: 40px;
    border: none;

    cursor: pointer;
    background-color: transparent;

    font: 500 0.9375rem/1 var(--font-family);

    & .link__angle {
      width: 10px;
      height: 6px;
      margin-left: 0.25rem;

      mask: no-repeat center/contain url('@/assets/angle.svg');
      background-color: #d4c4ed;
      transition: transform 0.15s ease;
      will-change: transform, background-color;

      &_toggle_yes {
        transform: rotate(-180deg);
      }

      .active & {
        background-color: var(--primary-color-text);
      }
    }

    .links__list-item.active &,
    .links__list-item:hover &,
    .links__list-item:focus &,
    .links__list-item:focus-visible & {
      color: var(--primary-color-text);
    }
  }

  &.active,
  &:hover,
  &:focus,
  &:focus-visible {
    color: var(--primary-color-text);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    background-color: #3e3e52;
  }

  &__icon {
    display: block;
    width: 24px;
    height: 24px;
    margin-right: 8px;

    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    background-color: #d4c4ed;

    will-change: background-color;

    &_type_swap {
      mask-image: url('@/assets/swap-wide.svg');
    }

    &_type_earn {
      mask-image: url('@/assets/earn.svg');
    }

    &_type_bridge {
      mask-image: url('@/assets/bridge.svg');
    }

    &_type_claim {
      mask-image: url('@/assets/claim.svg');
    }

    &_type_stats {
      mask-image: url('@/assets/stats.svg');
    }

    &_type_cl {
      mask-image: url('@/assets/concentrated-liquidity.svg');
    }

    .active & {
      background-color: var(--primary-color-text);
    }
  }
}
</style>

<style lang="scss">
.big.p-menu.p-menu-overlay {
  min-width: 300px !important;
}

.link__dropdown.p-menu {
  padding: 0.75rem 0.5rem;

  border-radius: 0.75rem;
  background-color: #28253e;

  & .p-menuitem {
    position: relative;
  }

  & .p-menuitem-link {
    padding: 0.75rem;
    border-radius: 0.5rem;

    transition: background-color 0.15s ease, color 0.15s ease;
    will-change: background-color, color;

    &.router-link-active,
    &:hover {
      background-color: #3e3e52;

      & .p-menuitem-text {
        color: var(--primary-color-text);
      }
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }

    & .p-menuitem-text {
      color: #fbfaff;

      transition: color 0.15s ease;
      will-change: color;
    }
  }

  & .menuitem-link-swap {
    &.router-link-active {
      background-color: #28253e;
    }
    &.extract-active-item {
      background-color: #3e3e52;
    }
    &:hover {
      background-color: #3e3e52;
    }
  }
}

.dropdown-link {
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;

  background-color: #d4c4ed;

  &_type_pools {
    width: 29px;
    height: 29px;
    margin-right: 1rem !important;

    mask-image: url('@/assets/pools.svg');
  }

  &_type_swap {
    width: 29px;
    height: 29px;
    margin-right: 1rem !important;

    mask-image: url('@/assets/swap-wide.svg');
  }

  &_type_swap-kana {
    width: 29px;
    height: 29px;
    margin-right: 1rem !important;

    mask-image: url('@/assets/kana-swap.svg');
  }

  &_type_farms {
    width: 32px;
    height: 26px;
    margin-right: 0.625rem !important;

    background: no-repeat center/contain url('@/assets/farms.svg');
  }

  &_type_staking {
    width: 22px;
    height: 22px;
    margin-right: 1.3125rem !important;

    mask-image: url('@/assets/staking.svg');
  }

  &_type_solo {
    width: 32px;
    height: 32px;
    margin-right: 0.625rem !important;

    background: no-repeat center/contain url('@/assets/pools-solo.svg');

    &:after {
      content: 'NEW';
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      background: linear-gradient(90deg, #6e42ca 0%, #8d29c1 100%);
      padding: 4px 12px;
      color: white;
      border-radius: 6px;

      font-family: Roboto;
      font-size: 13px;
      font-weight: 500;
      line-height: 16px;
      text-align: center;
    }
  }

  &_type_wormhole {
    width: 22px;
    height: 22px;
    margin-right: 1.3125rem !important;

    mask-image: url('@/assets/wormhole-header.svg');
  }

  &_type_layerzero {
    width: 22px;
    height: 22px;
    margin-right: 1.3125rem !important;

    mask-image: url('@/assets/layerzero-header.svg');
  }

  .router-link-active &_type_pools,
  .router-link-active &_type_staking {
    background-color: var(--primary-color-text);
  }

  .router-link-active &_type_farms {
    background-image: url('@/assets/farms-white.svg');
  }
}
</style>
